import * as docx from "docx";

// Shared builder for the agreement signee block in DOCX exports. Produces the
// same 2-column side-by-side layout the PDF route renders
// (routes/agreements.js signature-block rebuild): section label, org name,
// signature image (or placeholder), then Signer / Title / Date stacked.
//
// Built as docx objects with explicit 50% cell widths so the columns render
// side-by-side instead of the narrow "character-per-line" cells the HTML→DOCX
// walker (createTableFromElement) produces when cell widths are unset.

const {
  Table,
  TableRow,
  TableCell,
  Paragraph,
  TextRun,
  ImageRun,
  WidthType,
  BorderStyle,
  VerticalAlign,
  TableLayoutType,
} = docx;

const NO_BORDER = { style: BorderStyle.NONE, size: 0, color: "FFFFFF" };
const NO_BORDERS = {
  top: NO_BORDER,
  bottom: NO_BORDER,
  left: NO_BORDER,
  right: NO_BORDER,
  insideHorizontal: NO_BORDER,
  insideVertical: NO_BORDER,
};

// Decode a signature src (data: URL, or remote) to bytes for ImageRun. Kept
// self-contained so the Verification pages don't need their own decoder.
const getImageData = async (src) => {
  if (!src) return null;

  if (src.startsWith("data:")) {
    const base64 = src.split(",")[1];
    if (!base64) return null;
    try {
      const binary = window.atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      return bytes;
    } catch (e) {
      return null;
    }
  }

  try {
    const response = await fetch(src);
    const buffer = await response.arrayBuffer();
    return new Uint8Array(buffer);
  } catch (e) {
    return null;
  }
};

// Parse a single signature card — same shape the PDF route reads.
const parseCard = (card) => {
  const headerEl = card.firstElementChild;
  const headerSpans = headerEl
    ? Array.from(headerEl.querySelectorAll("span"))
    : [];
  const sectionLabel = headerSpans[0] ? headerSpans[0].textContent.trim() : "";

  const contentArea = card.children[1];
  const orgName =
    contentArea && contentArea.firstElementChild
      ? contentArea.firstElementChild.textContent.trim()
      : "";

  const sigImg = card.querySelector("img");

  const fields = {};
  Array.from(card.querySelectorAll("div, p")).forEach((row) => {
    const spans = Array.from(row.querySelectorAll("span"));
    if (spans.length >= 2) {
      const label = spans[0].textContent.trim().replace(/:$/, "").toLowerCase();
      const value = spans[1].textContent.trim();
      if (label === "signer") fields.Signer = value;
      else if (label === "title") fields.Title = value;
      else if (label === "date") fields.Date = value;
    }
  });

  return { sectionLabel, orgName, sigImg, fields };
};

const buildCell = async (card) => {
  const { sectionLabel, orgName, sigImg, fields } = parseCard(card);
  const children = [];

  if (sectionLabel) {
    children.push(
      new Paragraph({
        spacing: { after: 80 },
        children: [
          new TextRun({
            text: sectionLabel.toUpperCase(),
            bold: true,
            size: 16,
            color: "444444",
          }),
        ],
      })
    );
  }

  if (orgName) {
    children.push(
      new Paragraph({
        spacing: { after: 140 },
        children: [new TextRun({ text: orgName, bold: true, size: 30 })],
      })
    );
  }

  // Signature: embed the image when present/decodable, else a placeholder.
  let imageData = null;
  if (sigImg) {
    imageData = await getImageData(sigImg.getAttribute("src"));
  }
  if (imageData) {
    children.push(
      new Paragraph({
        spacing: { after: 140 },
        children: [
          new ImageRun({
            data: imageData,
            transformation: { width: 180, height: 60 },
          }),
        ],
      })
    );
  } else {
    children.push(
      new Paragraph({
        spacing: { after: 140 },
        children: [
          new TextRun({
            text: sigImg ? "Signature" : "Pending counter-signature",
            italics: true,
            color: "AAAAAA",
          }),
        ],
      })
    );
  }

  children.push(
    new Paragraph({
      spacing: { after: 40 },
      children: [
        new TextRun({ text: "Signer: ", size: 24 }),
        new TextRun({ text: fields.Signer || "", bold: true, size: 24 }),
      ],
    })
  );
  children.push(
    new Paragraph({
      spacing: { after: 40 },
      children: [
        new TextRun({ text: "Title: ", size: 24 }),
        new TextRun({ text: fields.Title || "", size: 24 }),
      ],
    })
  );
  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: "Date: " + (fields.Date || "— pending —"),
          size: 24,
        }),
      ],
    })
  );

  return new TableCell({
    width: { size: 50, type: WidthType.PERCENTAGE },
    verticalAlign: VerticalAlign.TOP,
    margins: { top: 80, bottom: 80, left: 120, right: 120 },
    borders: NO_BORDERS,
    children,
  });
};

// Build the 2-column signee Table from a `#fdf8ee` signature block element.
// Returns null when there is no signature block (nothing is appended).
export async function buildSignatureDocxTable(sigBlockEl) {
  if (!sigBlockEl) return null;

  const flexDiv = sigBlockEl.firstElementChild;
  if (!flexDiv) return null;

  const cards = Array.from(flexDiv.children);
  if (!cards.length) return null;

  const cells = [];
  for (const card of cards) {
    cells.push(await buildCell(card)); // eslint-disable-line no-await-in-loop
  }

  const tableOptions = {
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: NO_BORDERS,
    rows: [new TableRow({ children: cells })],
  };
  if (TableLayoutType && TableLayoutType.FIXED) {
    tableOptions.layout = TableLayoutType.FIXED;
  }

  return new Table(tableOptions);
}
